import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/_shared/masked-fields";
import { useGetFeeScheduleTemplatesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import {
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { getFeeScheduleTemplateQueryByInterface } from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import {
  FeeScheduleTagChip,
  TemplateList,
  TemplateLoader,
} from "work/fee-schedule-templates/_views/_shared/embedded-containers";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssue, ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FeeScheduleRedlineSelection from "work/proposals/redlining/field-redlines/_views/fee-schedule-redline-selection";
import ProposalRedline from "work/proposals/redlining/proposal-redline";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(() => ({
  display: "flex",
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "25rem",
  overflow: "hidden",
}));
const FeeScheduleContent = styled(Grid)(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
}));
const TemplatePanel = styled('div', {
  shouldForwardProp: (prop) => prop !== "open"
})<{ open?: boolean }>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: open ? 0 : 'initial',
}));
const TemplateContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
}));
const RedlineCategorySelection = styled(FeeScheduleRedlineSelection)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  flex: 1,
  height: "100%",
  minHeight: 0,
  marginLeft: theme.spacing(0),
  marginTop: theme.spacing(1),
  minWidth: 0,
  overflow: "hidden",
}));
const IssueContainer = styled("div")<{ active?: boolean }>(({ theme, active }) => ({
  animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
  margin: active ? theme.spacing(1) : "initial",
  marginLeft: 0,
  marginBottom: theme.spacing(1),
  padding: active ? theme.spacing(1) : "initial",
  paddingLeft: theme.spacing(1),
  paddingTop: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
}));

type FeeScheduleTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  disableEditing?: boolean;
  isDrawerOpen: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  onCommentsClicked: (field: ProposalField, name?: string) => void;
  onCreateCategoryClicked: () => void;
  onExistingCategoryClicked: () => void;
  onFeeScheduleTemplatesUpdated: (templateIds: Guid[], newProposalRedline?: ProposalRedline) => void;
};

const FeeScheduleTab = (props: Readonly<FeeScheduleTabProps>) => {
  const INTERFACE_NAME = "ActiveProposalFeeScheduleTab";

  const {
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    isDrawerOpen,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCommentsClicked,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onFeeScheduleTemplatesUpdated,
  } = props;

  const [expandedTemplateIds, setExpandedTemplateIds] = useState<Set<string>>(new Set());
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Guid[]>([]);

  const issues: ProposalIssue[] = React.useMemo(() => {
    return (
      props.issues?.entries.filter((issue) =>
        [ProposalField.FeeSchedule, ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.EMPTY)].includes(
          issue.field,
        ),
      ) ?? []
    );
  }, [props.issues?.entries]);

  const isLargeDownDisplay = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const session = useSession();

  const paginationParameters = new PaginationParameters();
  const filterParameters = new FeeScheduleTemplateFilterParameters({ entityId: session.user!.legalEntityId });
  const orderParameters = new FeeScheduleTemplateOrderParameters({
    orderBy: FeeScheduleTemplateOrderField.Name,
    orderDir: OrderDirection.Descending,
  });
  const defaultTemplateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery({
    filterParameters,
    orderParameters,
    paginationParameters: paginationParameters ?? PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
  });

  const templateQueryParams = useSelector(getFeeScheduleTemplateQueryByInterface(INTERFACE_NAME)) ?? defaultTemplateQuery;
  const templatesQuery = useGetFeeScheduleTemplatesQuery({ query: templateQueryParams, interfaceName: INTERFACE_NAME });
  const paginatedTemplatesJSON = templatesQuery.data;
  const templateFeeSchedules = useMemo(() => {
    if (!templatesQuery.isSuccess) return [];
    return FeeScheduleTemplate.fromJSONArray(paginatedTemplatesJSON?.data ?? []);
  }, [paginatedTemplatesJSON?.data, templatesQuery.isSuccess]);

  function handleTemplateExpanded(templateId?: string) {
    if (!templateId) return;
    let updatedExpandedTemplateIds = new Set(expandedTemplateIds);
    if (updatedExpandedTemplateIds.has(templateId)) {
      updatedExpandedTemplateIds.delete(templateId);
    } else {
      updatedExpandedTemplateIds.add(templateId);
    }
    setExpandedTemplateIds(updatedExpandedTemplateIds);
  }

  function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    if (!template.id) throw new Error("No template selected");

    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot modify fee schedule when it is masked.");
    }

    try {
      let updatedSelectedTemplateIds = [...selectedTemplateIds];
      const isAlreadySelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));

      let newProposalRedline: ProposalRedline | undefined = undefined;

      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
      } else {
        updatedSelectedTemplateIds.push(template.id);

        let newFeeScheduleRedline: FeeScheduleRedline = proposalRedline.feeSchedule;
        for (const category of template.categories) {
          newFeeScheduleRedline = newFeeScheduleRedline.addEntry(category);
        }
        newProposalRedline = proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline);
      }

      setSelectedTemplateIds(updatedSelectedTemplateIds);
      onFeeScheduleTemplatesUpdated(updatedSelectedTemplateIds, newProposalRedline);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to apply template.", { variant: "error" });
    }
  }

  function handleTemplateCategoryToggled(
    event: React.MouseEvent,
    templateCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null,
  ) {
    event.stopPropagation();
    if (Masked.isMasked(proposalRedline.feeSchedule)) throw new Error("Cannot modify fee schedule when it is masked.");

    if (!templateCategory) return;

    if (getIsCategoryAlreadySelected(templateCategory)) {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.removeEntryByFieldId(templateCategory.id),
      );

      onProposalRedlineChange(updatedRedline, null);
    } else {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.addEntry(templateCategory),
      );
      onProposalRedlineChange(updatedRedline, null);
    }
  }

  function getIsCategoryAlreadySelected(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null,
  ): boolean {
    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot check category when fee schedule is masked.");
    }

    if (!category) return false;
    return proposalRedline.feeSchedule.redlines.some(
      (categoryRedline) => categoryRedline.field.id?.isEqualTo(category.id) && categoryRedline.currentEntry !== null,
    );
  }

  function renderTemplates() {
    return (
      <TemplateList dense subheader={<ListSubheader>Templates</ListSubheader>}>
        {templateFeeSchedules?.length === 0 && (
          <>
            {templatesQuery.isLoading ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {templateFeeSchedules?.map((template: FeeScheduleTemplate) => {
          if (!template?.id?.value) return null;
          const isSelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));
          const feeScheduleCategories = [...template.categories];
          const indeterminate =
            !isSelected && feeScheduleCategories.some((category) => category && getIsCategoryAlreadySelected(category));

          return (
            <div key={template.id.value}>
              <ListItem
                secondaryAction={
                  <Tooltip title={`${expandedTemplateIds.has(template.id.value) ? "Hide" : "Show"} Categories`}>
                    <span>
                      <IconButton edge="end" onClick={() => handleTemplateExpanded(template.id?.value)} size="medium">
                        {expandedTemplateIds.has(template.id.value) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  <Tooltip title={isSelected ? "Deselect Template" : "Select Template"}>
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={isSelected}
                        indeterminate={indeterminate}
                        tabIndex={-1}
                        disableRipple
                        onChange={() => handleTemplateSelectionToggled(template)}
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.tags.map((tag) => (
                    <FeeScheduleTagChip key={tag.value} size="small" label={tag.value} />
                  ))}
                />
              </ListItem>
              <Collapse in={expandedTemplateIds.has(template.id.value)} timeout="auto" style={{ backgroundColor: "#FAFAFA" }}>
                <Divider />
                <List disablePadding dense>
                  {renderTemplateCategories(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateCategories(template: FeeScheduleTemplate) {
    return template.categories.map((category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => (
      <ListItem
        key={category.name.value}
        dense
        onClick={(event) => handleTemplateCategoryToggled(event, category)}
        secondaryAction={
          <Checkbox
            edge="end"
            color="primary"
            size="small"
            checked={getIsCategoryAlreadySelected(category)}
            tabIndex={-1}
            disableRipple
            onClick={(event) => handleTemplateCategoryToggled(event, category)}
          />
        }
      >
        <ListItemText
          primary={category.name.value}
          secondary={
            <>
              {category.description?.value}
              <br />
              {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
            </>
          }
        />
      </ListItem>
    ));
  }

  if (Masked.isMasked(proposalRedline.feeSchedule)) {
    return "You do not have permission to view the fee schedule.";
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <>
          <TemplatePanel hidden={isDrawerOpen}>
            <TemplateContainer>
              {renderTemplates()}
              {isLargeDownDisplay && <Divider />}
            </TemplateContainer>
          </TemplatePanel>
          {!isLargeDownDisplay && (
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          )}
        </>
      )}
      <FeeScheduleContent size="grow">
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <RedlineCategorySelection
          activeReviewField={activeReviewField}
          commentThreads={commentThreads}
          disableEditing={disableEditing}
          feeScheduleRedline={proposalRedline.feeSchedule}
          onCommentsClicked={onCommentsClicked}
          onCreateCategoryClicked={onCreateCategoryClicked}
          onExistingCategoryClicked={onExistingCategoryClicked}
          onFeeScheduleRedlineChange={(
            newFeeScheduleRedline: FeeScheduleRedline,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            try {
              onProposalRedlineChange(
                proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline),
                traversalFieldOverride,
              );
            } catch (error) {
              console.error(error);
              enqueueSnackbar("Failed to update fee schedule. Please try again", {
                variant: "error",
              });
            }
          }}
          onCategoryClicked={(categoryId: Guid) => {
            onTraverseToNewField?.(ProposalField.FeeScheduleCategory(categoryId));
          }}
        />
      </FeeScheduleContent>
    </ListContainer>
  )
};

export default FeeScheduleTab;
